/**
 * Lightweight reference extractors — turn a cypher node (or list) into
 * a thin chip-friendly shape. Shared between hierarchy endpoints and
 * the still-inline compute/PLC/alarms endpoints, so they live in the
 * package rather than the domain.
 */

import { extractNode } from './graph-row'
import { compareByNameThenId } from './sort'
import type {
  PlantComputeHost,
  PlantComputeRef,
  PlantController,
  PlantControllerRef,
  PlantGatewayRef,
  PlantRobotController,
  PlantRobotControllerRef
} from './types'

/**
 * Project a Cypher-returned `Compute` node onto the legacy `PlantGatewayRef`
 * shape the live-tag plumbing expects. The Compute node carries `url` /
 * `compute_type` (per the SCADA-ontology spec) but `LiveTagPanel` and
 * `getPlantLiveTagValues` still pass `gateway_url` / `gateway_type` along
 * to the proxy, so we alias here. We accept either field name on the
 * input so older nodes that already use `gateway_url` keep working.
 */
export function extractGateway(value: unknown): PlantGatewayRef | null {
  const base = extractNode<PlantGatewayRef>(value)
  if (!base) return null
  const raw = base as unknown as Record<string, unknown>
  const gatewayUrl =
    typeof raw['gateway_url'] === 'string' && raw['gateway_url']
      ? (raw['gateway_url'] as string)
      : typeof raw['url'] === 'string' && raw['url']
        ? (raw['url'] as string)
        : undefined
  const gatewayType =
    typeof raw['gateway_type'] === 'string' && raw['gateway_type']
      ? (raw['gateway_type'] as string)
      : typeof raw['compute_type'] === 'string' && raw['compute_type']
        ? (raw['compute_type'] as string)
        : undefined
  const project =
    typeof raw['project'] === 'string' && raw['project'] ? (raw['project'] as string) : undefined
  return {
    ...base,
    gateway_url: gatewayUrl,
    gateway_type: gatewayType,
    project
  }
}

/**
 * Cypher `collect(distinct ctrl)` of `(c)-[:MAPS_TO_CONTROLLER]->(:Controller)`
 * neighbours from `getPlantComputeIndex` / `getPlantComputeDetail`.
 * Projects each onto a {@link PlantControllerRef}, drops sentinel nulls
 * from `OPTIONAL MATCH`, and name-sorts for stable rendering.
 */
export function extractControllerRefs(value: unknown): PlantControllerRef[] {
  if (!Array.isArray(value)) return []
  const out: PlantControllerRef[] = []
  for (const item of value) {
    const node = extractNode<PlantController>(item)
    if (!node) continue
    out.push({
      id: node.id,
      name: node.name,
      ...(node.type ? { type: node.type } : {})
    })
  }
  out.sort(compareByNameThenId)
  return out
}

/**
 * Projects a single `Controller` node (from a `CONTROLLED_BY` edge in
 * `getPlantEquipmentDetail` / `getPlantLineDetail`) onto a
 * {@link PlantControllerRef}, or `null` when the `OPTIONAL MATCH` missed.
 */
export function extractControllerRef(value: unknown): PlantControllerRef | null {
  const node = extractNode<PlantController>(value)
  if (!node) return null
  return {
    id: node.id,
    name: node.name,
    ...(node.type ? { type: node.type } : {})
  }
}

export function extractComputeRefs(value: unknown): PlantComputeRef[] {
  if (!Array.isArray(value)) return []
  const out: PlantComputeRef[] = []
  const seen = new Set<string>()
  for (const item of value) {
    const node = extractNode<PlantComputeHost>(item)
    if (!node || seen.has(node.id)) continue
    seen.add(node.id)
    out.push({
      id: node.id,
      name: node.name,
      ...(node.compute_type ? { compute_type: node.compute_type } : {})
    })
  }
  out.sort(compareByNameThenId)
  return out
}

export function extractRobotControllerRefs(value: unknown): PlantRobotControllerRef[] {
  if (!Array.isArray(value)) return []
  const out: PlantRobotControllerRef[] = []
  const seen = new Set<string>()
  for (const item of value) {
    const node = extractNode<PlantRobotController>(item)
    if (!node || seen.has(node.id)) continue
    seen.add(node.id)
    out.push({
      id: node.id,
      name: node.display_name ?? node.name,
      ...(node.display_name ? { display_name: node.display_name } : {}),
      ...(node.vendor ? { vendor: node.vendor } : {}),
      ...(node.model ? { model: node.model } : {}),
      ...(node.needs_review ? { needs_review: true } : {})
    })
  }
  out.sort(compareByNameThenId)
  return out
}
